import React, { useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	FormHelperText,
	FormLabel,
	Grid,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { Form, useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import BrokerageLogo from '@/components/views/SettingsView/BrokerageSettings/Fields/BrokerageLogo';
import BrokerageColours from '@/components/views/SettingsView/BrokerageSettings/Fields/BrokerageColours';
import BrokerageAddress from '@/components/views/SettingsView/BrokerageSettings/Fields/BrokerageAddress';
import BrokerageFormField from '@/components/views/SettingsView/BrokerageSettings/Fields/BrokerageFormField';
import BrokerageNBStages from '@/components/views/SettingsView/BrokerageSettings/Fields/BrokerageNBStages';
import BrokerageUsers from '@/components/views/SettingsView/BrokerageSettings/Fields/BrokerageUsersTable';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';

const PROFILE_SAVE_KEYS = [
	'name',
	'claimsEmail',
	'contactEmail',
	'website',
	'phoneNumber',
	'fspNumber',
	'about',
	'disclosureUrl',
	'termsOfEngagement',
	'privacyStatement',
	'streetAddress',
	'suburb',
	'city',
	'postcode',
];

const BROKERAGE_TEXT_FIELDS = [
	'claimsEmail',
	'contactEmail',
	'website',
	'phoneNumber',
	'fspNumber',
	'about',
	'disclosureUrl',
	'termsOfEngagement',
	'privacyStatement',
];

export const BrokerageProfileDrawerForm = ({ canEdit }) => {
	const { values, initialValues, getFieldProps, isSubmitting } =
		useFormikContext();
	const [isLogoUploading, setIsLogoUploading] = useState(false);

	const account = useSelector((state) => state.msalAccount.account);

	const { data: currentUser } = useGetUserDetailsQuery(
		{ email: account?.username },
		{ skip: !account?.username },
	);

	const canViewGroup = Boolean(currentUser?.canViewGroup);

	const { getFeatureFlags } = useFeatureFlagContext();

	const showNewBusinessKPI = Boolean(
		getFeatureFlags('newBusinessKPI')?.active,
	);

	const isUpdating = isSubmitting || isLogoUploading;

	const isProfileDirty =
		PROFILE_SAVE_KEYS.some(
			(key) => values[key] !== initialValues[key],
		) ||
		!isEqual(values.colours ?? {}, initialValues.colours ?? {}) ||
		!isEqual(
			values.conversionRates ?? {},
			initialValues.conversionRates ?? {},
		);

	return (
		<Form
			style={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				minHeight: 0,
				width: '100%',
				height: '100%',
				overflow: 'hidden',
			}}
		>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: 'auto',
					py: 2,
				}}
			>
				<Stack spacing={2}>
					<Stack alignItems='center' spacing={1} sx={{ textAlign: 'center' }}>
						<BrokerageLogo
							brokerGroupId={values.docId}
							canEdit={canEdit}
							onUploadingChange={setIsLogoUploading}
						/>
						<Box>
							{/*font weight 700 is same as user */}
							<Typography variant='h5' fontWeight={700}>
								{values.name ?? 'Brokerage profile'}
							</Typography>
						</Box>
					</Stack>

					<BrokerageColours canEdit={canEdit} />

					<Card
						variant='outlined'
						sx={{ backgroundColor: 'transparent' }}
					>
						<CardContent>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormLabel>Name</FormLabel>

									<TextField
										fullWidth
										name='name'
										disabled={!canEdit}
										{...getFieldProps('name')}
									/>
								</Grid>

								<BrokerageAddress canEdit={canEdit} />

								{BROKERAGE_TEXT_FIELDS.map((field) => (
									<BrokerageFormField
										key={field}
										field={field}
										canEdit={canEdit}
									/>
								))}

								{showNewBusinessKPI && (
									<BrokerageNBStages canEdit={canEdit} />
								)}
							</Grid>
						</CardContent>
					</Card>

					{canViewGroup && (
						<BrokerageUsers brokerGroupId={values.docId} />
					)}
				</Stack>
			</Box>

			<FolioDrawerFormGuard />
			{canEdit && (
				<FolioDrawerFooter>
					<Button
						type='submit'
						variant='contained'
						disabled={!isProfileDirty || isUpdating}
					>
						Save
					</Button>
				</FolioDrawerFooter>
			)}
		</Form>
	);
};

export default BrokerageProfileDrawerForm;